import styled, { css } from 'styled-components';

import Link from '../../UI/Link/Link';
import Image from '../../UI/Image/Image';
import variables from '../../../styles/variables';
import { CssClassName } from '../../../utils/generateCssClassName/types';

interface Props {
  footerColor?: string;
  noPadding?: boolean;
  noMargin?: boolean;
  isKioskMode?: boolean;
  locale?: string;
  $footerWrapper?: boolean;
}

export const FooterWrapper = styled.footer`
  position: relative;
  width: 100%;
  min-height: 280px;
  z-index: 3;
  background-color: ${variables.colorGreyExtraDark};
  margin-top: ${({ $footerWrapper }: Props) => $footerWrapper ? 'clamp(50px, 7.5vw, 300px)' : '0'};

  @media screen and (max-width: 767px) {
    padding-bottom: ${({ $footerWrapper }: Props) => $footerWrapper ? '50px' : '0'};
  }

  &::before {
    position: absolute;
    bottom: calc(100% - 2px);
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: clamp(50px, 7.5vw, 300px) 0 0 calc(100vw);
    border-color: ${({ footerColor }: Props) => footerColor || 'transparent'}
      transparent transparent ${variables.colorGreyExtraDark};
    z-index: -1;
    outline: transparent solid 1px;
    pointer-events: none;
    content: none;
  }
`;

export const LinkList = styled.li`
  &.${CssClassName.KIOSK} {
    display: none;
  }

  &.why-choose-us {
    display: none;

    @media screen and (max-width: 1280px) {
      display: block;
    }
    @media screen and (max-width: 1024px) {
      display: none;
    }
  }
`;

// Allows you to "stretch" sections with a background colour behind the footer, so that you don't get an ugly white gap
export const Spacer = styled.div`
  ${({ noPadding }: Props) => !noPadding
    && css`
      padding-bottom: clamp(50px, 7.5vw, 300px);
    `}
  ${({ noMargin }: Props) => !noMargin
    && css`
      margin-bottom: clamp(-300px, -7.5vw, -50px);
    `}
  margin-top: 1rem;
`;

export const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 60% 40%;
  grid-template-rows: repeat(3, 1fr);
  align-items: flex-start;
  grid-template-areas:
    'logo links'
    'icons links'
    'a11y links';
  max-width: ${variables.defaultWidth}px;
  padding: 40px ${variables.layoutPadding}rem 0;
  margin: 0 auto;
  @media screen and (max-width: 1023px) {
    padding: 40px ${variables.mobileLayoutPadding}rem 0;
    grid-template-columns: 50% 50%;
  }
  @media screen and (max-width: 767px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 4.125rem auto;
    grid-template-areas:
      'logo'
      'links'
      'icons'
      'a11y';
    padding-bottom: 50px;
  }
`;

export const LogoLink = styled(Link)`
  display: flex;
  width: 100%;
  margin: 0;
`;

export const StyledImage = styled(Image)`
  margin: 0;
  width: auto;
  height: 44px;
`;

export const Navigation = styled.nav`
  grid-area: links;
  ul {
    list-style-type: none;
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: 0;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    li {
      margin: 10px 0;
      width: 45%;
      color: ${variables.colorWhite};
      /* This is because this field in the CMS is currenrtly rich text, and extra tags may come down the pipe */
      * {
        color: inherit;
        font-size: 14px;
        line-height: 1.4;
      }
      a {
        color: ${variables.colorWhite};
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
  @media screen and (max-width: 767px) {
    margin: 40px 0;
  }
`;

export const SocialContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-start;
  grid-area: icons;
`;

export const A11yContainer = styled(SocialContainer)`
  grid-area: a11y;

  ${({ isKioskMode, locale }: Props) => (isKioskMode || locale === 'fr')
    && css`
      grid-area: icons;
      margin-top: 20px;
      @media screen and (max-width: 767px) {
        margin: 0;
      }
    `}
`;

export const CookieSettingsButton = styled.button`
  color: rgb(255, 255, 255);
  background: transparent;
  border: none;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;
